import { RemoteDataPage } from '@lycoris-nubila/ngx-lycoris';
import { Observable } from 'rxjs';

import { UserEmailModelOutput } from 'infrastructure/graphql/content/user/model/user-email-model.output';
import { UserModuleAnswerOutput } from 'infrastructure/graphql/content/user/model/user-module-answer.output';
import { UserModuleStatisticOutput } from 'infrastructure/graphql/content/user/model/user-module-statistic.output';
import { UserModuleSuggestionOutput } from 'infrastructure/graphql/content/user/model/user-module-suggestion.output';
import { UserModuleOutput } from 'infrastructure/graphql/content/user/model/user-module.output';
import { UserPhaseStatsOutput } from 'infrastructure/graphql/content/user/model/user-phase-stats.output';
import { MessagingUserModulePushOutput } from 'infrastructure/graphql/messaging/user/model/messaging-user-module-push.output';
import { MessagingUserSettingOutput } from 'infrastructure/graphql/messaging/user/model/messaging-user-setting.output';
import { UserModulePushOutput } from 'infrastructure/graphql/messaging/user/model/user-module-push.output';
import { UserUsageStatsOutput } from 'infrastructure/graphql/messaging/user/model/user-usage-stats.output';
import { OrganizationOutput } from 'organization/model/organization.output';
import { AssociateCoachWithCoacheeCmd } from 'user/command/associate-coach-with-coachee.cmd';
import { ConnectToUserNetworkCmd } from 'user/command/connect-to-user-network.cmd';
import { CreateUserCmd } from 'user/command/create-user.cmd';
import { GenerateUserCVCmd } from 'user/command/generate-user-cv.cmd';
import { CreateUserModuleAnswersDocumentCmd } from 'user/command/module/answer/create-user-module-answers-document.cmd';
import { GenerateUserModuleAnswersCmd } from 'user/command/module/answer/generate-user-module-answers.cmd';
import { ResendUserModulePushCmd } from 'user/command/module/push/resend-user-module-push.cmd';
import { UpdateUserModuleCoachCommentCmd } from 'user/command/module/update-user-module-coach-comment.cmd';
import { UpdateUserModuleRatingCmd } from 'user/command/module/update-user-module-rating.cmd';
import { SaveUserMoodCmd } from 'user/command/mood/save-user-mood.cmd';
import { CreateUserNoteCmd } from 'user/command/note/create-user-note.cmd';
import { ReconnectToUserNetworkCmd } from 'user/command/reconnect-to-user-network.cmd';
import { SaveUserModuleSpentDurationCmd } from 'user/command/save-user-module-spent-duration.cmd';
import { SynchronizeUserNetworkSelectedCalendarsCmd } from 'user/command/synchronize-user-network-selected-calendars.cmd';
import { UpdateCoacheeObserversCmd } from 'user/command/update-coachee-observers.cmd';
import { UpdateUserSettingsCmd } from 'user/command/update-user-settings.cmd';
import { CreateUserTaskExportTaskCmd } from 'user/command/userTaskExportTask/create-user-task-export-task.cmd';
import { Auth0ConnectionOutput } from 'user/model/auth0-connection.output';
import { ExternalCalendarInformationOutput } from 'user/model/external-calendar-information.output';
import { IamUserTaskExportTaskOutput } from 'user/model/iam-user-task-export-task.output';
import { StatisticsUserOutput } from 'user/model/statistics-user.output';
import { UserAvailableSlotOutput } from 'user/model/user-available-slot.output';
import { UserMfaEngageOutput } from 'user/model/user-mfa-engage.output';
import { UserMoodOutput } from 'user/model/user-mood.output';
import { UserNetworkState } from 'user/model/user-network.state';
import { UserNetworkType } from 'user/model/user-network.type';
import { UserNoteOutput } from 'user/model/user-note.output';
import { UserStatisticsOutput } from 'user/model/user-statistics.output';
import { UserTaskExportTaskStatus } from 'user/model/user-task-export-task-status';
import { UserOutput } from 'user/model/user.output';
import { FetchOwnedUserModulePushesQuery } from 'user/query/fetch-owned-user-module-pushes.query';
import { FetchUserModulePushesQuery } from 'user/query/module/push/fetch-user-module-pushes.query';
import { ObserveSearchedUserCoachNotesQuery } from 'user/query/note/observe-searched-user-coach-notes.query';
import { CanExportUserMeetingsQuery } from 'user/query/task-export-task/can-export-user-meetings.query';
import { SearchUserTaskExportTasksQuery } from 'user/query/task-export-task/search-user-task-export-tasks.query';

import { SaveUserAnswersCmd } from '../command/module/answer/save-user-answers.cmd';
import { UpdateUserModuleCmd } from '../command/module/update-user-module.cmd';
import { UpdateCoachCmd } from '../command/update-coach.cmd';
import { UpdateUserAvailabilitiesCmd } from '../command/update-user-availabilities.cmd';
import { UpdateUserBiographyCmd } from '../command/update-user-biography.cmd';
import { UpdateUserCmd } from '../command/update-user.cmd';
import { FetchUserAvailableSlotsQuery } from '../query/fetch-user-available-slots.query';
import { ObserveUserAnswersByModulesIdQuery } from '../query/module/answer/observe-user-answers-by-modules-id.query';
import { ObserveUserAnswersByQuestionsIdQuery } from '../query/module/answer/observe-user-answers-by-questions-id.query';
import { ObserveSearchedCoachesIncludingPartnersQuery } from '../query/observe-searched-coaches-including-partners.query';

export abstract class UserApiPort {
	public abstract markMoodAsked(): Observable<void>;

	public abstract fetchUserModuleSuggestions(): Observable<
		UserModuleSuggestionOutput[]
	>;

	public abstract markMoodDescriptionShown(): Observable<void>;

	public abstract fetchUserConnectedDaysCount(): Observable<number>;

	public abstract fetchModulesStatistic(): Observable<
		UserModuleStatisticOutput[]
	>;

	public abstract fetchStatisticsUser(): Observable<StatisticsUserOutput>;

	public abstract createUser(cmd: CreateUserCmd): Promise<UserOutput>;

	public abstract updateUserGrantNewsLetter(
		grantNewsLetter: boolean
	): Observable<void>;

	public abstract fetchOrganizationsByIds(
		ids: string[]
	): Observable<OrganizationOutput[]>;

	public abstract fetchMessagingUserSettings(
		ownerId: string
	): Observable<MessagingUserSettingOutput[]>;

	public abstract validateUserMfa(code: string): Observable<void>;

	public abstract disableUserMfa(): Observable<void>;

	public abstract generateUserMfa(): Observable<UserMfaEngageOutput>;

	public abstract disassociateCoachWithCoachee(
		coacheeId: string
	): Observable<string>;

	public abstract fetchCoachCoachees(): Promise<UserOutput[]>;

	public abstract resendUserModulePush(
		cmd: ResendUserModulePushCmd
	): Promise<UserModulePushOutput[]>;

	public abstract addModuleToPlan(
		userId: string,
		moduleId: string
	): Promise<string>;

	public abstract associateUserWithCoach(coachId: string): Promise<UserOutput>;

	public abstract deleteModuleFromPlan(
		userId: string,
		moduleId: string
	): Promise<void>;

	public abstract deleteUserModuleAnswers(
		userId: string,
		moduleId: string
	): Promise<string[]>;

	public abstract fetchUserPendingMessages(
		coacheesId?: string[]
	): Promise<{ [key: string]: number }>;

	public abstract searchCoachesIncludingPartners(
		query: ObserveSearchedCoachesIncludingPartnersQuery
	): Promise<RemoteDataPage<UserOutput>>;

	public abstract fetchUserAnswersByQuestionsId(
		query: ObserveUserAnswersByQuestionsIdQuery
	): Promise<UserModuleAnswerOutput[]>;

	public abstract fetchUserAvailableSlots(
		query: FetchUserAvailableSlotsQuery
	): Promise<UserAvailableSlotOutput[]>;

	public abstract fetchUserModulePushes(
		query: FetchUserModulePushesQuery
	): Observable<UserModulePushOutput[]>;

	public abstract fetchUserById(id: string): Observable<UserOutput>;

	public abstract fetchUsersByIds(ids: string[]): Promise<UserOutput[]>;

	public abstract fetchUserByJwToken(): Observable<UserOutput>;

	public abstract fetchUserModuleAnswersByModulesId(
		query: ObserveUserAnswersByModulesIdQuery
	): Promise<UserModuleAnswerOutput[]>;

	public abstract fetchUserModulesByUserId(
		userId: string
	): Promise<UserModuleOutput[]>;

	public abstract fetchLastMood(): Observable<UserMoodOutput>;

	public abstract fetchUserNetworkInfo(
		auth0UserId: string
	): Promise<Auth0ConnectionOutput>;

	public abstract fetchUserPhasesStats(
		userId: string
	): Promise<UserPhaseStatsOutput[]>;

	public abstract fetchUserUsageStats(): Promise<UserUsageStatsOutput>;

	public abstract fetchUserStatistics(): Promise<UserStatisticsOutput>;

	public abstract generateUserAvatarUrl(file: File): Promise<string>;

	public abstract saveUserAnswers(
		cmd: SaveUserAnswersCmd
	): Promise<UserModuleAnswerOutput[]>;

	public abstract saveUserMood(cmd: SaveUserMoodCmd): Observable<string>;

	public abstract updateCoach(cmd: UpdateCoachCmd): Promise<UserOutput>;

	public abstract updateCoachBiography(
		cmd: UpdateUserBiographyCmd
	): Promise<UserOutput>;

	public abstract updateUser(cmd: UpdateUserCmd): Promise<UserOutput>;

	public abstract updateUserAvailabilities(
		cmd: UpdateUserAvailabilitiesCmd
	): Promise<UserOutput>;

	public abstract updateUserAvatar(avatarUrl: string): Promise<UserOutput>;

	public abstract updateUserModule(
		cmd: UpdateUserModuleCmd
	): Promise<UserModuleOutput>;

	public abstract updateUserModuleCoachComment(
		cmd: UpdateUserModuleCoachCommentCmd
	): Promise<UserModuleOutput>;

	public abstract updateUserModuleRating(
		cmd: UpdateUserModuleRatingCmd
	): Promise<UserModuleOutput>;

	public abstract updateUserQuote(quote: string): Promise<UserOutput>;

	public abstract updateUserLanguage(lang: string): Promise<UserOutput>;

	public abstract fetchUserEmailModels(): Observable<UserEmailModelOutput[]>;

	public abstract createUserModuleAnswersDocument(
		cmd: CreateUserModuleAnswersDocumentCmd
	): Promise<boolean>;

	public abstract generateUserModuleAnswers(
		cmd: GenerateUserModuleAnswersCmd
	): Promise<string>;

	public abstract updateCoacheeObservers(
		cmd: UpdateCoacheeObserversCmd
	): Observable<UserOutput>;

	public abstract createUserNote(cmd: CreateUserNoteCmd): Observable<string>;

	public abstract ownedUserNotes(userId: string): Observable<UserNoteOutput[]>;

	public abstract deleteUserNote(noteId: string): Observable<string>;

	public abstract associateCoachWithCoachee(
		cmd: AssociateCoachWithCoacheeCmd
	): Observable<UserOutput>;

	public abstract fetchCoacheesWithoutCoach(
		ids: string[]
	): Promise<UserOutput[]>;

	public abstract updateMessagingUserSettings(
		cmd: UpdateUserSettingsCmd
	): Observable<MessagingUserSettingOutput[]>;

	public abstract isUserNewsletterEnabled(): Observable<boolean>;

	public abstract generateUserCV(cmd: GenerateUserCVCmd): Observable<string>;

	public abstract searchUserCoachNotes(
		query: ObserveSearchedUserCoachNotesQuery
	): Observable<UserNoteOutput[]>;

	public abstract mustAskMood(): Observable<boolean>;

	public abstract mustAnswerOnBoardingModule(): Observable<boolean>;

	public abstract mustAnswerFirstCoachingPlanModule(): Observable<boolean>;

	public abstract saveUserModuleSpentDuration(
		cmd: SaveUserModuleSpentDurationCmd
	): Observable<void>;

	public abstract fetchOwnedMoods(): Observable<UserMoodOutput[]>;

	public abstract fetchUserDaysUntilNextLevelUpCount(): Observable<number>;

	public abstract reconnectToUserNetwork(
		cmd: ReconnectToUserNetworkCmd
	): Observable<void>;

	public abstract disconnectFromUserNetwork(
		networkType: UserNetworkType
	): Observable<void>;

	public abstract userNetworkState(
		networkType: UserNetworkType
	): Observable<UserNetworkState>;

	public abstract synchronizeUserNetworkSelectedCalendars(
		cmd: SynchronizeUserNetworkSelectedCalendarsCmd
	): Observable<void>;

	public abstract userExternalCalendar(
		networkType: UserNetworkType
	): Observable<ExternalCalendarInformationOutput[]>;

	public abstract connectToUserNetwork(
		cmd: ConnectToUserNetworkCmd
	): Observable<void>;

	public abstract markUserModuleDescriptionAsVisualized(
		coachingPlanModuleId: string
	): Observable<string>;

	public abstract countUserModulePushesByStatusApiQuery(
		userId: string
	): Observable<{
		[key: string]: number;
	}>;

	public abstract generateUserPushModuleAnswersSynthesis(
		coacheeId: string
	): Observable<void>;

	public abstract createUserTaskExportTask(
		cmd: CreateUserTaskExportTaskCmd
	): Observable<IamUserTaskExportTaskOutput>;

	public abstract searchUserTaskExportTask(
		query: SearchUserTaskExportTasksQuery
	): Observable<IamUserTaskExportTaskOutput[]>;

	public abstract canExportUserMeetings(
		query: CanExportUserMeetingsQuery
	): Observable<UserTaskExportTaskStatus>;

	public abstract ownedUserModulePushes(
		query: FetchOwnedUserModulePushesQuery
	): Observable<MessagingUserModulePushOutput[]>;
}
